/* eslint-disable react/no-array-index-key */
/* eslint-env browser */

import styled from '@emotion/styled';
import { Config, FormApi, MutableState } from 'final-form';
import { Box, ScrollIntoView } from 'localmed-core';
import compose from 'lodash/flowRight';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { graphql } from 'react-apollo';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router';
import { UniversalContext } from '../../../../../_universal';
import { CREATE_APPOINTMENT_MUTATION } from '../../../../../shared/components/AppointmentForm/AppointmentFormWithData';
import WIDGET_SESSION_SCHEDULED_MUTATION from '../../../../../shared/components/AppointmentForm/WidgetSessionScheduledMutation.graphql';
import { DEPENDENT_STATUS } from '../../../../../shared/components/AppointmentForm/constants';
import { saveNewUserSession } from '../../../../../shared/components/AppointmentForm/newUserSession';
import submitAppointment from '../../../../../shared/components/AppointmentForm/submitAppointment';
import { SchedulingSessionStateContext } from '../../../../../shared/components/SchedulingSessionState';
import {
  APPOINTMENT_STATUS,
  DILM_CLIENT_CONTEXT,
  FLASH_MESSAGES,
  LM_CLIENT_CONTEXT,
  OTHER_ID,
} from '../../../../../shared/constants';
import { trackAffiliateEvent } from '../../../../../shared/utils/affiliateTracking';
import { buildAppointmentUrl } from '../../../../../shared/utils/urls';
import markRequestAsScheduled from '../../utils';
import MARK_REQUEST_AS_SCHEDULES from './markRequestAsScheduled.graphql';
import {
  ISchedulingValues,
  IState,
  IStepElement,
  IWizardController,
  Response,
  TrackSchedulingEvent,
} from './types';
import { isCorrectableSubmitFailure } from './utils';

const Container = styled(Box)`
  min-height: 100%;
`;

interface SchedulingWizardProps {
  initialValues: ISchedulingValues;
  validateAppointmentForm?: (
    values: ISchedulingValues,
    provider?: { profile?: { agesTreated?: { max?: number } } }
  ) => Promise<any>;
  trackSchedulingEvent?: TrackSchedulingEvent;
  children: React.ReactNode;
}

function SchedulingWizard({
  initialValues,
  validateAppointmentForm,
  trackSchedulingEvent,
  children,
  ...props
}: SchedulingWizardProps) {
  const history = useHistory();
  const { trackEvent, trackError } = useContext(UniversalContext);
  const { widgetSettings } = useContext(SchedulingSessionStateContext);
  const submitCountRef = useRef(0);
  const [values, setValues] = useState<IState>({
    step: 0,
    maxStep: 0,
    lastKnownValues: initialValues || {},
  });

  const steps: IStepElement[] = React.Children.toArray(children).filter(
    (component): component is React.ReactElement =>
      component != null && typeof component !== 'string'
  );
  const { step: currentStepNumber, lastKnownValues } = values;
  const currentStep = steps[currentStepNumber];
  if (currentStep == null) {
    throw new Error(`Could not find a component for step ${currentStepNumber}`);
  }

  const getStepById = (stepId: string) => {
    const stepNumber = steps.findIndex((step) => step.props.id === stepId);
    if (stepNumber === -1) return undefined;

    return stepNumber;
  };

  const getStepResult = async (wizardStep: IStepElement, currentValues: ISchedulingValues) => {
    let { completed } = wizardStep.props;

    if (typeof completed === 'function') {
      try {
        completed = await completed(currentValues);
      } catch (error) {
        completed = false;
      }
    }
    return { completed: !!completed };
  };

  const updateStepResults = async (formValues: ISchedulingValues) => {
    const stepResults = await Promise.all(steps.map((s) => getStepResult(s, formValues)));
    const maxStep = stepResults.findIndex((stepResult) => !stepResult.completed);
    if ((maxStep > values.maxStep || maxStep > currentStepNumber) && currentStep.props.autoStep) {
      setValues({ maxStep, step: currentStepNumber + 1, lastKnownValues: formValues });
    } else {
      setValues((currValues) => ({ ...currValues, maxStep, lastKnownValues: formValues }));
    }
  };

  const canGotoStep = (step: number | string | undefined): boolean => {
    const stepNumber = typeof step === 'string' ? getStepById(step) : step;
    if (stepNumber == null) return false;

    return stepNumber >= 0 && stepNumber <= values.maxStep;
  };

  const gotoStep = (step: number | string | undefined) => {
    const stepNumber = typeof step === 'string' ? getStepById(step) : step;
    if (stepNumber == null || !canGotoStep(stepNumber)) {
      throw new Error(`Cannot goto step ${step}`);
    }
    setValues((currValues) => ({ ...currValues, step: stepNumber }));
  };

  const gotoPreviousStep = () => {
    gotoStep(values.step - 1);
  };

  const gotoNextStep = () => {
    gotoStep(values.step + 1);
  };

  const isFirstPage = () => values.step === 0;

  const isLastPage = () => values.step === steps.length - 1;

  const onSubmit = async (formValues: ISchedulingValues, form: FormApi) => {
    const response: Response = await submitAppointment(formValues, props);
    const { appointment } = response;

    // Using a temp value because operator assignment is not working
    const tempCount = submitCountRef.current;
    submitCountRef.current = tempCount + 1;

    // Track events
    const submittedAppointmentEvent = {
      event: 'Submitted Appointment',
      properties: {
        ...pick(response, ['isSuccessful', 'status', 'message', 'errors']),
        submitCount: submitCountRef.current,
      },
    };
    trackEvent(submittedAppointmentEvent);

    if (!response.isSuccessful || appointment == null) {
      const errors = response.errors || {};
      if (errors.opening) {
        gotoStep('SCHEDULING_TIME_SELECTION_STEP');
        return errors;
      }
      const patientInfoFormScreen = getStepById('PATIENT_INFO_FORM_SCREEN');
      if (patientInfoFormScreen != null && isCorrectableSubmitFailure(errors)) {
        gotoStep(patientInfoFormScreen);
      }
      return errors;
    }

    if (formValues.createAccount) {
      saveNewUserSession(response);
    }

    form.change('appointment', appointment);
    const {
      scheduleRequest,
      // contactMobilePhone,
      reasonForVisit,
      insuranceIssuer,
      dependentStatus,
      createAccount,
      provider,
      office,
      practice,
      patientStatus,
    } = formValues;

    const isForDependent = dependentStatus === DEPENDENT_STATUS.DEPENDENT;
    const scheduledAppointmentEvent = {
      event: 'Scheduled Appointment',
      properties: {
        // contactMobilePhone,
        appointmentId: appointment?.id,
        providerId: provider?.id,
        providerDisplay: provider?.display,
        officeId: office?.id,
        officeDisplay: office?.display,
        practiceId: practice?.id,
        practiceDisplay: practice?.display,
        reasonForVisitId: reasonForVisit?.id,
        reasonForVisitDisplay: reasonForVisit?.display,
        patientStatusId:
          typeof patientStatus === 'string' ? patientStatus : patientStatus?.id.toLowerCase(),
        insuranceIssuerId:
          insuranceIssuer && insuranceIssuer.id !== OTHER_ID ? insuranceIssuer.id : null,
        insuranceIssuerDisplay:
          insuranceIssuer && insuranceIssuer.id !== OTHER_ID ? insuranceIssuer.display : null,
        createdAccount: createAccount,
        createdDependent: createAccount && isForDependent,
        scheduledForDependent: isForDependent,
        submitCount: submitCountRef.current,
      },
    };
    trackEvent(scheduledAppointmentEvent);
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      ...scheduledAppointmentEvent,
    });

    if (scheduleRequest) {
      const markRequestAsScheduledResponse = await markRequestAsScheduled(
        formValues,
        appointment.id,
        props
      );

      if (markRequestAsScheduledResponse.status === 'FAILED') {
        trackError({
          error: markRequestAsScheduledResponse.errors,
          properties: { component: 'SchedulingWizard' },
        });
      }
    }

    if (appointment && appointment.status === APPOINTMENT_STATUS.PENDING && !scheduleRequest) {
      const appointmentUrl = buildAppointmentUrl(provider!.id, appointment.id, {
        patientToken: appointment.patientToken,
        params: {
          message:
            appointment.status === APPOINTMENT_STATUS.PENDING ? FLASH_MESSAGES.SCHEDULED : '',
          exp_widget_v2: true, // Used for the experimentation period
        },
      });
      history.push(appointmentUrl);
    } else {
      gotoNextStep();
    }

    return undefined;
  };

  const createWizardController = (form: FormApi, handleSubmit: VoidFunction): IWizardController => {
    const formValues = form.getState().values as ISchedulingValues;
    if (!isEqual(lastKnownValues, formValues)) {
      // updateSteps when form changes
      updateStepResults(formValues);
    }
    return {
      step: currentStepNumber,
      steps,
      gotoStep,
      gotoNextStep,
      gotoPreviousStep,
      hasPreviousStep: !isFirstPage(),
      previousButtonProps: {
        disabled: isFirstPage(),
        onClick: () => gotoPreviousStep(),
      },
      hasNextStep: !isLastPage(),
      nextButtonProps: {
        disabled: !canGotoStep(currentStepNumber + 1),
        onClick: () => gotoNextStep(),
      },
      form,
      values: form.getState().values as ISchedulingValues,
      handleSubmit,
      trackSchedulingEvent,
    };
  };

  const validate = async (formValues: any) => {
    let errors: any;
    if (validateAppointmentForm) {
      await validateAppointmentForm(formValues, formValues.provider)
        .then(() => {
          errors = {};
        })
        .catch((e) => {
          errors = e;
        });

      return errors;
    }
    return {};
  };

  const setFieldTouched = (args: any[], state: MutableState<any, any>) => {
    const [name, touched] = args;
    const field = state.fields[name];

    if (field) {
      field.touched = !!touched;
    }
  };

  useEffect(() => {
    if (initialValues) {
      updateStepResults(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      validateOnBlur
      onSubmit={onSubmit as Config['onSubmit']}
      mutators={{
        setFieldTouched,
      }}
      render={({ form, handleSubmit }) => {
        const wizard = createWizardController(form, handleSubmit);
        return (
          <Container
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mb="md"
            minHeight="100%"
          >
            <ScrollIntoView key={currentStepNumber} />
            {React.cloneElement(currentStep, { wizard })}
          </Container>
        );
      }}
    />
  );
}

const SchedulingWizardComponent: React.ComponentType<SchedulingWizardProps> = compose(
  graphql(CREATE_APPOINTMENT_MUTATION, {
    name: 'createAppointment',
    alias: 'withCreateAppointment',
    options: { context: LM_CLIENT_CONTEXT },
  }),
  graphql(MARK_REQUEST_AS_SCHEDULES, {
    name: 'markScheduleRequestAttemptAsScheduled',
    alias: 'withMarkScheduleRequestAttemptAsScheduled',
    options: { context: DILM_CLIENT_CONTEXT },
  }),
  graphql(WIDGET_SESSION_SCHEDULED_MUTATION, {
    name: 'widgetSessionScheduled',
    alias: 'withWidgetSessionScheduled',
    options: { context: DILM_CLIENT_CONTEXT },
  })
)(SchedulingWizard);

export default SchedulingWizardComponent;
